import { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'
import CurrencyContext from 'contexts/currencyContext'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import useExperienceAvailableDates from 'hooks/ExperiencePage/useExperienceAvailableDates'
import useExperienceAvailableTimes from 'hooks/ExperiencePage/useExperienceAvailableTimes'
import { useExperienceTicketsOptions } from 'hooks/ExperiencePage/useExperienceTicketsOptions'
import useCollinsonAutoTicketMode from 'hooks/ExperiencePage/useCollinsonAutoTicketMode'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { EXP_CATEGORY_AIRPORT_TRANSFER_ID, EXP_CATEGORY_AIRPORT_LOUNGE_ID, EXP_CATEGORY_WATER_TRANSFER_ID, EXP_CATEGORY_PRIVATE_TRANSFER_ID, EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW, EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID } from 'constants/experience'
import { arrayToObject, min, sum } from 'lib/array/arrayUtils'
import { formToObject } from 'lib/forms/formToObject'
import type { FetchDateParams } from 'lib/experiences/experienceUtils'
import { getExperienceDurationString, getTotalSeats } from 'lib/experiences/experienceUtils'
import { getDefaultTicketModes } from 'lib/experiences/ticketModeUtils'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { formatMiddleDotSeparator } from 'LuxKit/intl/separators'
import { EmptyObject, mergeObjects } from 'lib/object/objectUtils'
import Accordion from 'LuxKit/Accordion'
import CalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import Caption from 'LuxKit/Typography/Caption'
import ClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import Divider from 'LuxKit/Divider'
import Heading from 'LuxKit/Typography/Heading'
import ImageGallery from 'LuxKit/Carousel/ImageGallery'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import Typography from 'LuxKit/Typography/Typography'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import ExperienceCancellationText from 'components/Experiences/ExperienceCancellationPolicy/ExperienceCancellationText'
import { MAP_TITLE_TO_EVENT } from 'components/Experiences/ExperienceOfferPage/ExperienceOfferPageContent'
import ExperienceTicketSelection from 'components/Experiences/ExperienceTickets/ExperienceTicketSelection'
import ExperienceTicketInputs from 'components/Experiences/ExperienceTickets/ExperienceTicketInputs'
import ExperienceTicketCustomerLimit from 'components/Experiences/ExperienceTickets/ExperienceTicketCustomerLimit'
import ExperienceDetailsAboutLocationMap from './About/ExperienceDetailsAboutLocationMap'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import ExperienceBookingModalPrices from '../ExperienceBookingModalPrices'
import LayoutContainer from 'components/Common/LayoutContainer'

const Footer = styled.div`
  display: flex;
  width: 100%;
  gap: ${rem(12)};
  justify-content: space-between;

  flex-direction: column;

  >:last-child {
    width: 100%;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;

    >:last-child {
      width: auto;
    }
  }
`

const FooterPrice = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const FadingElement = styled.div`
  display: none;
  min-width: ${rem(120)};

  &.visible {
    display: block;
  }

  ${mediaQueryUp.tablet} {
    display: block;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: none;

    &.visible {
      display: block;
      opacity: 1;
      pointer-events: auto;
    }
  }
`

const SubmitButtonContainer = styled.div`
  min-width: ${rem(120)};
`

const SpacedTicketSelection = styled(ExperienceTicketSelection)`
  margin-top: ${rem(32)};
`

const SpacedVerticalSpacer = styled(VerticalSpacer)`
  margin-top: ${rem(16)};
`

const ImageContainer = styled.div`
  height: ${rem(280)};

  & > * {
    height: 100%;
    width: 100%;
  }
`

const TRANSFER_AND_LOUNGE_CATEGORIES = [
  EXP_CATEGORY_AIRPORT_TRANSFER_ID,
  EXP_CATEGORY_AIRPORT_LOUNGE_ID,
  EXP_CATEGORY_WATER_TRANSFER_ID,
  EXP_CATEGORY_PRIVATE_TRANSFER_ID,
  EXP_CATEGORY_PHOTOSHOOT_TRAVELSHOOT_ID,
]

export interface ExperienceDetailsForm {
  time?: string
  language?: string
  date?: string
  pickupPoint?: string
  redemptionLocation?: string
  ticketModesMap?: Record<string, string>
  ticketMode?: string
  ticketCount?: { [id: string]: number } // Not Used
}

interface Props {
  experienceView: App.ExperienceBookingView
  defaultPickupPoint?: App.ExperiencePickupPoint
  defaultLocation?: App.ExperienceRedemptionLocation
  defaultDate?: string
  defaultTimeSlot?: string
  defaultTicketCounts?: Record<string, number>
  defaultLanguage?: App.ExperienceLanguage
  experienceDates?: App.ExperienceDatesState
  experienceTimes?: App.ExperienceTimeSlotsState
  defaultTicketModesMap?: Record<string, string>
  currencyCode?: string
  checkInDate?: string
  autoSelectDate?: string
  checkOutDate?: string
  onSubmit: (view: App.ExperienceBookingView, counts: Record<string, number>, params: ExperienceDetailsForm, tickets: Array<App.ExperienceItemTicket>) => void
  onClose: (params: ExperienceDetailsForm) => void
  disableRemove?: boolean
  propertyLocation: { latitude: number | undefined, longitude: number | undefined } | { latitude: undefined, longitude: undefined }
  isLuxPlusEnabled: boolean
  ctaText?: string
  maxCheckInTime?: string
  maxCheckOutTime?: string
  checkInLabel?: string | React.ReactNode
  checkOutLabel?: string | React.ReactNode
}

function ExperienceDetailsBookingModal(props: Props) {
  const {
    experienceView,
    onClose,
    defaultTicketCounts = EmptyObject,
    defaultDate,
    defaultPickupPoint,
    defaultLocation,
    defaultTimeSlot,
    defaultLanguage,
    defaultTicketModesMap,
    onSubmit,
    checkInDate,
    autoSelectDate,
    checkOutDate,
    disableRemove,
    propertyLocation,
    isLuxPlusEnabled,
    ctaText = 'Add',
    maxCheckInTime,
    maxCheckOutTime,
    checkInLabel,
    checkOutLabel,
  } = props

  const intl = useIntl()
  const totalSaleUnitLabel = intl.formatMessage({
    id: 'IUKURVRuwE4kXDcIZN3V',
    defaultMessage: 'total',
    description: 'Default sale unit label for pricing in experience booking modal footer',
  })
  const currency = useContext(CurrencyContext)
  const [selectedPickupPoint, setSelectedPickupPoint] = useState<App.ExperiencePickupPoint | undefined>(defaultPickupPoint)
  const [selectedLocation, setSelectedLocation] = useState<App.ExperienceRedemptionLocation | undefined>(defaultLocation)
  const [selectedTicketModesMap, setSelectedTicketModesMap] = useState<Record<string, string> | undefined>(defaultTicketModesMap)
  const [ticketCounts, setTicketCounts] = useState<Record<string, number>>(defaultTicketCounts)
  const [date, setDate] = useState<string | undefined>(defaultDate)
  const [timeSlot, setTimeSlot] = useState<string | undefined>(defaultTimeSlot)
  const [hasNoTicketsSelected, setHasNoTicketsSelected] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)
  const trackEvent = useTrackExperienceOfferEvent()
  const formId = useId()
  const experience = experienceView.originalExperience
  const isBuyNowBookLater = experienceView.isBuyNowBookLater
  const isGift = experienceView.isGift

  const collinsonAutoTicketMode = useCollinsonAutoTicketMode({
    provider: experience.provider,
    hasCalendar: experience.hasCalendar,
  })

  const fetchDateParams = useMemo(() => ({
    pickupPointId: selectedPickupPoint?.id,
    redemptionLocationId: selectedLocation?.id,
    ticketModesMap: selectedTicketModesMap,
    isGift,
    isBuyNowBookLater,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, isBuyNowBookLater, isGift])
  const [availableDates, fetchingDates] = useExperienceAvailableDates(experienceView.originalExperience, fetchDateParams)

  const fetchTimeParams = useMemo(() => ({
    date,
    redemptionLocationId: selectedLocation?.id,
    pickupPointId: selectedPickupPoint?.id,
    ticketModesMap: selectedTicketModesMap,
    isBuyNowBookLater,
    isGift,
  }), [selectedPickupPoint, selectedLocation, selectedTicketModesMap, date, isBuyNowBookLater, isGift])
  const [availableTimes, fetchingTimes] = useExperienceAvailableTimes(experienceView.originalExperience, fetchTimeParams)

  const loading = !!(fetchingDates || fetchingTimes)

  const currentTimeSlot = useMemo(() => (availableTimes ?? []).find(slot => slot.time === timeSlot), [availableTimes, timeSlot])

  const removeTickets = useCallback(() => {
    setTicketCounts({})

    if (formRef.current) {
      const ticketData = formToObject<ExperienceDetailsForm>(formRef.current)
      delete ticketData.ticketCount
    }
  }, [setTicketCounts])

  useEffect(() => {
    if (!experience) return

    const fetchParams: FetchDateParams = {}

    const locations = experience.redemptionLocations
    if (locations?.length === 1) {
      const defaultLocation = locations[0]
      setSelectedLocation(defaultLocation)
      fetchParams.redemptionLocationId = defaultLocation.id
      trackEvent('REDEMPTION_LOCATION_SELECTED_NON_INTERACTIVE')
    }

    const pickups = experience.pickupPoints
    if (pickups?.length === 1) {
      const defaultPickup = pickups[0]
      setSelectedPickupPoint(defaultPickup)
      fetchParams.pickupPointId = defaultPickup.id
      trackEvent('PICKUP_LOCATION_SELECTED_NON_INTERACTION')
    }
  }, [experienceView]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((availableDates?.length ?? 0) > 0 && !experience.hasCalendar) {
    // we still fetch calendar data even though it doesn't have it as we need it
    // to go fetch the times which gives us the tickets
      const date = availableDates[0].day
      setDate(date)
      trackEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
    }
  }, [isBuyNowBookLater, isGift, availableDates]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!date && autoSelectDate && availableDates?.length && availableDates.find(date => date.day === autoSelectDate)) {
      setDate(autoSelectDate)
      trackEvent('DATE_ON_CALENDAR_SELECTED_NON_INTERACTION')
    }
  }, [autoSelectDate, availableDates]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!collinsonAutoTicketMode) return

    const ticketModes = experience.ticketModes ?? []
    const defaults = getDefaultTicketModes(ticketModes)

    const domesticIntlField = ticketModes.find(field =>
      field.options.some(opt => opt.key === 'DOMESTIC') &&
      field.options.some(opt => opt.key === 'INTERNATIONAL'),
    )

    if (domesticIntlField) {
      defaults[domesticIntlField.key] = collinsonAutoTicketMode

      const selectedOption = domesticIntlField.options.find(opt => opt.key === collinsonAutoTicketMode)
      if (selectedOption?.fields) {
        Object.assign(defaults, getDefaultTicketModes(selectedOption.fields))
      }
    }

    setSelectedTicketModesMap(defaults)
  }, [collinsonAutoTicketMode]) // oxlint-disable-line react-hooks/exhaustive-deps -- experience.ticketModes is stable for the modal's lifetime

  const onPickupPointSelect = useCallback((newPickupPoint?: App.ExperiencePickupPoint) => {
    setSelectedPickupPoint(newPickupPoint)
    setDate('')
    setTimeSlot('')
    removeTickets()
  }, [removeTickets])

  const onLocationSelect = useCallback((newLocation?: App.ExperienceRedemptionLocation) => {
    setSelectedLocation(newLocation)
    setDate('')
    setTimeSlot('')
    removeTickets()
  }, [removeTickets])

  const onTicketModeSelect = useCallback((newTicketMode?: Record<string, string>) => {
    setSelectedTicketModesMap(newTicketMode)
    setDate('')
    setTimeSlot('')
    removeTickets()
  }, [removeTickets])

  const onDateSelect = useCallback((newDate: App.ExperienceAvailabilityDate) => {
    setDate(newDate.day)
    setTimeSlot('')
    removeTickets()
  }, [removeTickets])

  const onSelectTimeSlot = useCallback((newTimeSlot: string) => {
    if (newTimeSlot === timeSlot) return
    setTimeSlot(newTimeSlot)
    removeTickets()
  }, [setTimeSlot, timeSlot, removeTickets])

  const onTicketCountChange = useCallback((increment: boolean) => {
    if (increment) {
      return trackEvent('INCREASE_OPTION_SELECTED')
    }
    return trackEvent('DECREASE_OPTION_SELECTED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const handleTicketCountChange = useCallback((ticket: App.ExperienceBookingView['ticketOptions'][number], nextCount: number) => {
    if (hasNoTicketsSelected) setHasNoTicketsSelected(false)
    setTicketCounts(prevTicketCounts => ({
      ...prevTicketCounts,
      [ticket.id]: nextCount,
    }))
    onTicketCountChange((ticketCounts[ticket.id] ?? 0) < nextCount)
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- onTicketCountChange is stable
  }, [ticketCounts, hasNoTicketsSelected])

  const allTicketOptions = useMemo(() => {
    return (availableTimes ?? []).flatMap(slot => slot.tickets)
  }, [availableTimes])

  const [isFromPrice, totalPrice, totalMemberPrice, totalGrossPrice] = useMemo(() => {
    const ticketsById = arrayToObject(allTicketOptions, ticket => ticket.id)

    const cheapestTicket = min(allTicketOptions, ticket => ticket.price)
    const selectedTicketPrices = Object.entries(ticketCounts).flatMap(([id, count]) => (ticketsById[id]?.price ?? 0) * count)
    const selectedPrice = sum(selectedTicketPrices)

    const selectedTicketGrossPrices = Object.entries(ticketCounts).flatMap(([id, count]) => {
      const ticket = ticketsById[id]
      if (!ticket) return 0
      return (ticket.price + (ticket.discounts.base.amount ?? 0)) * count
    })
    const selectedGrossPrice = sum(selectedTicketGrossPrices)

    let cheapestLuxPlusTicket: App.ExperienceItemTicket | undefined = undefined
    let selectedLuxPlusPrice = 0

    if (isLuxPlusEnabled) {
      cheapestLuxPlusTicket = min(allTicketOptions, ticket => ticket.memberPrice)
      const selectedLuxPlusTicketPrices = Object.entries(ticketCounts).flatMap(([id, count]) => (ticketsById[id]?.memberPrice ?? 0) * count)
      selectedLuxPlusPrice = sum(selectedLuxPlusTicketPrices)
    }

    return [
      selectedLuxPlusPrice === 0 && selectedPrice === 0,
      selectedPrice || cheapestTicket?.price || experienceView.price,
      selectedLuxPlusPrice || cheapestLuxPlusTicket?.memberPrice || experienceView.pricing.memberPrice,
      selectedGrossPrice || experienceView.pricing.value,
    ]
  }, [ticketCounts, experienceView, allTicketOptions, isLuxPlusEnabled])

  const hasDefaultTickets = useMemo(() => sum(Object.values(defaultTicketCounts)) > 0, [defaultTicketCounts])
  const counts = useMemo(() => {
    return Object.entries(ticketCounts).map(([ticketId, count]) => {
      return {
        ticket: allTicketOptions.find(t => t.id === ticketId),
        count,
      }
      // sometimes the tickets we previously selected are no longer available
      // remove them
    }).filter(count => !!count.ticket)
  }, [ticketCounts, allTicketOptions])

  const { nonExtraTicketCount, ticketCount } = useMemo(() => ({
    ticketCount: sum(counts, count => count.count),
    nonExtraTicketCount: sum(counts.filter(item => !item.ticket?.isExtra), count => count.count),
  }), [counts])

  const submit = useCallback((counts: Record<string, number>, ticketData: ExperienceDetailsForm) => {
    // If we're submitting the form it means we have tickets selected,
    // therefore there will be currentTimeSlot (for calendar-timeslots offers) or
    // at least one availableTimes (for bnbl/voucher offers)
    const selectedTimeSlot = currentTimeSlot ?? availableTimes[0]

    // Tickets have the same ID across all the available time slots.
    // By returning only the tickets of the selected time slot we ensure
    // that the correct ticket instance will be selected when finding by id
    const slotTickets = selectedTimeSlot.tickets

    onSubmit?.(experienceView, counts, ticketData, slotTickets)
  }, [currentTimeSlot, availableTimes, onSubmit, experienceView])

  const { totalUsedSeatsOnCart, totalSeatsRemaining, isWithinSessionLimit } = useMemo(() => {
    if (!currentTimeSlot) return { totalSeatsRemaining: 0, totalUsedSeatsOnCart: 0, totalUsedSeatsOnServer: 0, isWithinSessionLimit: true }
    return getTotalSeats(currentTimeSlot, ticketCounts)
  }, [ticketCounts, currentTimeSlot])

  const onAddOrUpdate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (nonExtraTicketCount === 0 || minSeats > totalUsedSeatsOnCart || !isWithinSessionLimit) {
      setHasNoTicketsSelected(true)
      return
    }

    const ticketData = formToObject<ExperienceDetailsForm>(e.currentTarget)
    delete ticketData.ticketCount

    fireEcommerceGTMEvent(addToCartGTM(experience, currency, totalPrice, ticketCount, 'experience'))

    // Removes tickets with 0 count
    const counts = mergeObjects(
      Object.entries(ticketCounts)
        .filter(([_, count]) => !!count)
        .map(([ticketType, count]) => ({ [ticketType]: count })),
    )

    ticketData.ticketModesMap = selectedTicketModesMap
    submit(counts, ticketData)
  }
  const onModalClose = useCallback(() => {
    if (formRef.current) {
      const ticketData = formToObject<ExperienceDetailsForm>(formRef.current)
      onClose(ticketData)
    }
  }, [onClose])

  const onRemoveTickets = useCallback(() => {
    removeTickets()
    if (formRef.current) {
      const ticketData = formToObject<ExperienceDetailsForm>(formRef.current)
      submit({}, ticketData)
    }
  }, [submit, removeTickets])
  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const availableTicketOptions = useExperienceTicketsOptions(availableTimes, { time: timeSlot })

  const canRemove = ticketCount > 0 && hasDefaultTickets && !disableRemove

  const minSeats = currentTimeSlot?.purchaseLimit.min ?? 1

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (isFromPrice) {
      return [{
        price: totalPrice,
        memberPrice: isLuxPlusEnabled ? totalMemberPrice : 0,
        offerId: experience.id,
        loyaltyProductType: 'experience',
      }]
    }

    const ticketsById = arrayToObject(allTicketOptions, ticket => ticket.id)

    const selectedTickets = Object.entries(ticketCounts).flatMap(([id, count]): Array<App.LoyaltyPointsEarnCalculationRequest> => {
      const ticket = ticketsById[id]
      if (!ticket) return []
      return Array.from({ length: count }, (): App.LoyaltyPointsEarnCalculationRequest => ({
        price: ticket.price || 0,
        memberPrice: isLuxPlusEnabled ? ticket.memberPrice : 0,
        loyaltyProductType: 'experience',
        offerId: experience.id,
      }))
    })

    return selectedTickets
  }, [allTicketOptions, ticketCounts, totalPrice, isFromPrice, totalMemberPrice, isLuxPlusEnabled, experience.id])

  const handleThingsToKnowOpen = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].open) {
      trackEvent(MAP_TITLE_TO_EVENT[title].open)
    }
  }, [trackEvent])

  const handleThingsToKnowClose = useCallback((title: string) => {
    if (MAP_TITLE_TO_EVENT[title].close) {
      trackEvent(MAP_TITLE_TO_EVENT[title].close)
    }
  }, [trackEvent])

  const hasDescription = useMemo(
    () => experience.copy.dealDescription && experience.copy.dealDescription.trim() !== experienceView.title.trim(),
    [experience.copy.dealDescription, experienceView.title],
  )

  const distanceFromPropertyToRedemptionLocation = useMemo(() => {
    if (!propertyLocation.latitude || !propertyLocation.longitude || !selectedLocation) return null
    return calculateDistance(propertyLocation.latitude, propertyLocation.longitude, selectedLocation.latitude, selectedLocation.longitude)
  }, [propertyLocation, selectedLocation])

  const isTransferOrLounge = experienceView.categories.some(category =>
    TRANSFER_AND_LOUNGE_CATEGORIES.includes(category.id),
  )

  const showDistanceText = useMemo(() => {
    return experienceView.distance && !isTransferOrLounge
  }, [experienceView.distance, isTransferOrLounge])

  const durationString = useMemo(() => getExperienceDurationString(experience, intl), [experience, intl])

  return (<ModalBase size="L" onClose={onModalClose}>
    <ModalHeader onCloseButtonClick={onModalClose} title="" isOverlay />
    <ModalBody>
      <ImageContainer>
        <ImageGallery
          size="small"
          images={experience.images}
          showImageTitles={false}
        />
      </ImageContainer>
      <ModalContent>
        <Group direction="vertical" gap={20} tabletGap={24}>
          <Group direction="vertical" gap={8}>
            <Typography wrap="truncate">
              {experienceView.location && <>
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">{experienceView.location}</Caption>
                <Caption as="span" variant="large" colour="neutral-one" weight="normal">{formatMiddleDotSeparator(intl)}</Caption>
              </>}
              <BodyText as="span" variant="small" colour="neutral-two">{experienceView.primaryCategory}</BodyText>
            </Typography>

            {showDistanceText && <Typography wrap="pretty">
              <Caption as="span" variant="large" colour="neutral-two" weight="normal">
                <FormattedMessage
                  id="C_2Le5x9iSfeQHNV2_vv"
                  defaultMessage="{distance} km from hotel"
                  description="Distance from hotel for an experience, e.g. '2.5 km from hotel'"
                  values={{
                    distance: <FormatDecimal maximumFractionDigits={experienceView?.distance && experienceView.distance > 1 ? 0 : 1} value={experienceView.distance ?? 0} />,
                  }}
                />
              </Caption>
            </Typography>}

            <Heading as="h3" variant="heading3">{experienceView.title}</Heading>

            <BodyTextBlock variant="medium" startIcon={<CalendarIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">{intl.formatMessage({
                  id: 'xt9mlan1b6DzZ9AsszuA',
                  defaultMessage: 'Availability:',
                  description: 'Bold label preceding the availability details in the experience booking modal',
                })}
                </BodyText> {experience.copy.availability}
              </BodyText>
            </BodyTextBlock>

            <BodyTextBlock variant="medium" startIcon={<ClockFiveIcon aria-hidden="true" />}>
              <BodyText variant="medium">
                <BodyText variant="medium" weight="bold" as="span">{intl.formatMessage({
                  id: '4S78p77ZqRA-qVyvWYL1',
                  defaultMessage: 'Duration:',
                  description: 'Form field label for experience duration in the booking modal',
                })}
                </BodyText> {durationString}
              </BodyText>
            </BodyTextBlock>

            <ExperienceCancellationText
              cancellationInfo={experience.copy.cancellationInfo}
            />
            {hasDescription && <LargeContentToggle
              maxHeight={80}
              showMoreText={intl.formatMessage({
                id: 'wTJyOBAZ3mjRTJV0PDZI',
                defaultMessage: 'Read more',
                description: 'Toggle text to expand deal description in experience booking modal',
              })}
              showLessText={intl.formatMessage({
                id: 'aFeW3ZIhCNsxyB60t0XH',
                defaultMessage: 'Read less',
                description: 'Toggle text to collapse deal description in experience booking modal',
              })}
            >
              <MarkdownRender content={experience.copy.dealDescription ?? ''} />
            </LargeContentToggle>}
          </Group>
          <Divider kind="primary" />
          {experience.copy.included?.trim() &&
            <>
              <div>
                <LargeContentToggle
                  maxHeight={80}
                  showMoreText={intl.formatMessage({
                    id: 'Njt2udRGoHUyURprJyX4',
                    defaultMessage: 'View more inclusions',
                    description: 'Button text to expand inclusions in experience booking modal',
                  })}
                  showLessText={intl.formatMessage({
                    id: 'U7qyiSzPyOn4LvQjny-P',
                    defaultMessage: 'View less inclusions',
                    description: 'Button text to collapse inclusions in experience booking modal',
                  })}
                >
                  <BodyText as="h6" variant="large" weight="bold">{intl.formatMessage({
                    id: 'WCl2GnLeDGBcSoDycJlV',
                    defaultMessage: 'Inclusions',
                    description: 'Section heading for inclusions in experience booking modal',
                  })}
                  </BodyText>
                  <MarkdownRender type="compact" content={experience.copy.included} />
                </LargeContentToggle>
              </div>
              <Divider kind="primary" />
            </>}
          <form onSubmit={onAddOrUpdate} ref={formRef} id={formId}>
            <div style={{ position: 'relative' }}>
              <LoadingIndicator floating visible={loading} />
              <Heading as="h4" variant="heading4">{intl.formatMessage({
                id: 'QLi5sYomEkSox7GIUc0_',
                defaultMessage: 'Pricing & availability',
                description: 'Section heading for pricing and availability in experience booking modal',
              })}
              </Heading>
              {experience && !experienceView.isBuyNowBookLater && <ExperienceTicketInputs
                onTimeSelect={onSelectTimeSlot}
                onPickupPointSelect={onPickupPointSelect}
                onLocationSelect={onLocationSelect}
                onTicketModeSelect={onTicketModeSelect}
                experienceView={experienceView}
                allDates={availableDates}
                availableTimes={availableTimes}
                onDateSelect={onDateSelect}
                selectedTimeSlot={currentTimeSlot ? timeSlot : ''}
                defaultPickupPointId={defaultPickupPoint?.id}
                defaultLanguageId={defaultLanguage?.id}
                defaultTicketModesMap={defaultTicketModesMap}
                defaultRedemptionLocationId={defaultLocation?.id}
                removeLocationLabel
                hideTicketModes={!!collinsonAutoTicketMode}
                isCollinson={experience.provider === 'cln'}
                selectedDate={date}
                checkInDate={checkInDate}
                checkOutDate={checkOutDate}
                maxCheckInTime={maxCheckInTime}
                maxCheckOutTime={maxCheckOutTime}
                checkInLabel={checkInLabel}
                checkOutLabel={checkOutLabel}
              />}
              {availableTicketOptions.length > 0 && !!currentTimeSlot && <SpacedVerticalSpacer gap={4}>
                <ExperienceTicketCustomerLimit
                  purchaseLimit={currentTimeSlot.purchaseLimit}
                  totalSeatsRemaining={totalSeatsRemaining}
                  totalUsedSeatsOnCart={totalUsedSeatsOnCart}
                />
                {((ticketCount > 0 && nonExtraTicketCount === 0) || hasNoTicketsSelected) && (
                  <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
                    {intl.formatMessage({
                      id: 'LnSeccx-Fad-vsFyP9Bu',
                      defaultMessage: 'You should add at least one experience ticket',
                      description: 'Warning message when no tickets selected in experience booking modal',
                    })}
                  </MessageBanner>
                )}
              </SpacedVerticalSpacer>}
              <SpacedTicketSelection
                onTicketCountChange={handleTicketCountChange}
                ticketOptions={availableTicketOptions}
                ticketCounts={ticketCounts}
                purchaseLimitRemaining={totalSeatsRemaining}
                purchaseLimitReason={currentTimeSlot?.purchaseLimit.maxReason}
              />
            </div>
          </form>
          <Divider kind="primary" />
          <div>
            <Accordion
              heading={intl.formatMessage({
                id: 'UqO7o-Ml8rxFih9o4mfY',
                defaultMessage: 'Location',
                description: 'Accordion heading for location section in experience booking modal',
              })}
              size="large"
              hideDivider
            >
              <ExperienceDetailsAboutLocationMap
                experienceView={experienceView}
                selectedLocation={selectedLocation}
                distanceFromProperty={distanceFromPropertyToRedemptionLocation}
              />
            </Accordion>
            {experience.copy.thingsToKnow.map(item => (
              <Accordion
                key={item.title}
                onOpen={() => handleThingsToKnowOpen(item.title)}
                onClose={() => handleThingsToKnowClose(item.title)}
                heading={item.title}
                size="large"
                hideDivider
              >
                <LayoutContainer>
                  <MarkdownRender content={item.content} type="compact" fontSize="medium" />
                </LayoutContainer>
              </Accordion>
            ))}
          </div>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <Footer>
        <FooterPrice>
          <Group direction="vertical" gap={8}>
            <ExperienceBookingModalPrices
              totalPrice={totalPrice}
              totalMemberPrice={totalMemberPrice}
              totalGrossPrice={totalGrossPrice}
              isFromPrice={isFromPrice}
              ticketUnitLabel={isFromPrice && experienceView.ticketUnitLabel ? experienceView.ticketUnitLabel : totalSaleUnitLabel}
              discountPercentage={isFromPrice && experienceView.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.discount : undefined}
              discountMemberPercentage={isFromPrice && experienceView.pricing.memberDiscountPercentage >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW ? experienceView.pricing.memberDiscountPercentage : undefined}
              isLuxPlusOffer={isLuxPlusEnabled && experienceView.pricing.memberPrice > 0}
            />
            <PriceRowAgentHubCommission
              offerId={experience.id}
              productType={productType}
              offerLocations={[experience.location.name]}
            />
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={pointsEarnCalculationRequest}
              calculationType={isFromPrice ? 'estimate' : 'exact'}
              hideStatusCredits
            />
          </Group>
        </FooterPrice>
        <Group direction="vertical" tabletDirection="horizontal" gap={8}>
          <FadingElement
            as={TextButton}
            aria-hidden={!canRemove}
            className={clsx({ visible: canRemove })}
            kind="tertiary"
            size="large"
            onClick={onRemoveTickets}
          >
            {intl.formatMessage({
              id: 'J4eToKeELIz7_saJAFMU',
              defaultMessage: 'Remove',
              description: 'Button text to remove tickets in experience booking modal',
            })}
          </FadingElement>
          <SubmitButtonContainer>
            <TextButton
              kind="primary"
              size="large"
              type="submit"
              fit="full-width"
              form={formId}
            >
              {ctaText}
            </TextButton>
          </SubmitButtonContainer>
        </Group>
      </Footer>
    </ModalFooter>
  </ModalBase>)
}

export default ExperienceDetailsBookingModal
